import React, { useState, useEffect } from "react";
import UserIcon from "../assets/images/login-flow/user.svg";
import PasswordIcon from "../assets/images/login-flow/password.svg";
import EmailIcon from "../assets/images/login-flow/mail.svg";
import PhoneIcon from "../assets/images/login-flow/phone.svg";
import { Link } from "react-router-dom";
import { Form, Button, Col, Row } from "react-bootstrap";
import "../scss/login.scss";

import { useDispatch, useSelector } from "react-redux";
import { register } from "../features/auth/authActions";
import { getPageContent } from "../features/visitor/visitorActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify"; // Import toast
import { formatPhoneNumber } from "../helpers/helpers";
import { Helmet } from "react-helmet-async";

function Register() {
  const [headerData, setHeaderData] = useState(null);
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const { loading, successMessage, errorMessage } = useSelector(
    (state) => state.auth
  );
  const { pageResponse } = useSelector((state) => state.visitor);

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    setPhone(formatPhoneNumber(value));
  };

  const validate = () => {
    const errors = {};

    // Check for empty fields
    if (!username) errors.username = "Username is required.";
    if (!fullName) errors.fullName = "Full Name is required.";
    if (!email) errors.email = "Email is required.";
    if (!phone) errors.phone = "Phone Number is required.";
    if (!password) errors.password = "Password is required.";
    if (password !== confirmPassword)
      errors.confirmPassword = "Passwords must match.";
    if (!termsAccepted)
      errors.termsAccepted = "You must accept the terms and conditions.";

    // Check for valid email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email))
      errors.email = "Invalid email format.";

    // Check password length
    if (password && password.length < 8)
      errors.password = "Password must be at least 8 characters long.";

    if (phone && phone.length !== 12)
      errors.phone = "Please enter valid phone number.";

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Initial blank errors
    setErrors({});

    // Validate form inputs
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Dismiss any currently visible toasts
    toast.dismiss();

    // Dispatch registration action
    let data = {
      username: username,
      name: fullName,
      email: email,
      mobile_number: phone,
      password: password,
      confirm_password: password,
      terms_conditions: termsAccepted ? 1 : 0,
    };

    dispatch(register(data));
  };

  // Show success and error toasts
  useEffect(() => {
    if (successMessage) {
      setUsername("");
      setFullName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setTermsAccepted(false);
      setErrors({});
      toast.success(successMessage, { autoClose: 3000, hideProgressBar: true });
    }
    if (errorMessage) {
      toast.error(errorMessage, { autoClose: 3000, hideProgressBar: true });
    }
  }, [successMessage, errorMessage]);

  useEffect(() => {
    dispatch(getPageContent());
  }, [dispatch]);

  useEffect(() => {
    if (pageResponse) {
      let footer = pageResponse.find((r) => r.code === "footer");
      setHeaderData(footer.footer_section);
    }
  }, [pageResponse]);

  return (
    <>
      <Helmet>
        <title>Sign Up | Helpotani</title>
        <meta
          name="description"
          content="Create a new Helpotani account to access personalized services and manage your orders."
        />
        <meta
          name="keywords"
          content="Sign Up, Register, Helpotani, Account, Create Account"
        />
      </Helmet>

      <section className="register">
        <div className="register-box">
          <Link to="/" className="logo-img">
            <img
              src={`${process.env.REACT_APP_PHP_BACKEND_MEDIA_URL}${headerData?.main_logo}`}
              alt=""
            />
          </Link>
          <h1>Register</h1>
          <p>Fill Required Details to Onboard in Personal Care.</p>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group controlId="formUsername" className="form-group">
                  <Form.Label>Username</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => {
                        let value = e.target.value.toLowerCase(); // Convert to lowercase
                        // Allow only letters, numbers, underscores (_), and at (@)
                        if (/^[a-z0-9_@]*$/.test(value)) {
                          setUsername(value);
                        }
                      }}
                      isInvalid={!!errors.username}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.username}
                    </Form.Control.Feedback>
                    <img src={UserIcon} alt="Username" />
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formFullName" className="form-group">
                  <Form.Label>Full Name</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="text"
                      placeholder="Full Name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      isInvalid={!!errors.fullName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fullName}
                    </Form.Control.Feedback>
                    <img src={UserIcon} alt="Username" />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formEmail" className="form-group">
                  <Form.Label>Email Address</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                    <img src={EmailIcon} alt="Email" />
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formPhoneNumber" className="form-group">
                  <Form.Label>Phone Number</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="tel"
                      placeholder="Phone Number"
                      maxLength={12}
                      value={phone}
                      onChange={handlePhoneChange}
                      isInvalid={!!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                    <img src={PhoneIcon} alt="Phone" />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group controlId="formPassword" className="form-group">
                  <Form.Label>Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                    <img src={PasswordIcon} alt="Password" />
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group
                  controlId="formConfirmPassword"
                  className="form-group"
                >
                  <Form.Label>Confirm Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      isInvalid={!!errors.confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmPassword}
                    </Form.Control.Feedback>
                    <img src={PasswordIcon} alt="Confirm Password" />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group
              className="mb-3 form-group"
              controlId="formBasicCheckbox"
            >
              <Form.Check
                className="custom-checkbox"
                type="checkbox"
                checked={termsAccepted ? "checked" : ""}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                isInvalid={!!errors.termsAccepted}
                label={
                  <span>
                    By continuing, you’ve read and agree to our
                    <a
                      href="/terms-condition"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {" "}
                      Terms and Conditions
                    </a>{" "}
                    and
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {" "}
                      Privacy Policy
                    </a>
                    .
                  </span>
                }
              />
            </Form.Group>

            <Form.Control.Feedback
              type="invalid"
              className={termsAccepted ? "" : "error-show"}
            >
              {errors.termsAccepted}
            </Form.Control.Feedback>

            <Button className="custom-bttn" type="submit" disabled={loading}>
              {loading ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  size="lg"
                  className="spinner-icon"
                />
              ) : (
                "Register"
              )}
            </Button>

            <Row>
              <Col className="text-center">
                <p className="login-footer-text">
                  Haven't created an account yet? &nbsp;
                  <Link to="/login" title="Login">
                    Login
                  </Link>{" "}
                </p>
              </Col>
            </Row>
          </Form>
        </div>
      </section>
    </>
  );
}

export default Register;
